import { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { urlEnum } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";

interface IntegrationsSettingsData {
  WORDPRESS_URL: string;
  APP_CONSUMER_KEY: string;
  APP_CONSUMER_SECRET: string;
}

export default function IntegrationsSettings() {
  const [settings, setSettings] = useState<IntegrationsSettingsData>({
    WORDPRESS_URL: "",
    APP_CONSUMER_KEY: "",
    APP_CONSUMER_SECRET: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    getSettings();
  }, []);



  const getSettings = async () => {
    // urlEum.wordpressSettings
    try {
      setLoading(true);
      getData(`${urlEnum.integrationsSettings}/wordpress`).then(
        (response: any) => {
          if (response && response.data && response.data.result)
            setSettings(response.data.result);
        }
      );
    } catch (error) {
      console.error("Failed to fetch WordPress settings:", error);
      setMessage({
        type: "error",
        text: Vocabulary.failedToLoadWordpressSettings,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof IntegrationsSettingsData, value: string) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      // Validate required fields
      if (!settings.WORDPRESS_URL || !settings.APP_CONSUMER_KEY || !settings.APP_CONSUMER_SECRET) {
        setMessage({
          type: "error",
          text: Vocabulary.allFieldsRequired,
        });
        return;
      }

      const response = await postData(`${urlEnum.integrationsSettings}/wordpress`, settings);

      if (response?.status === 201) {
        setMessage({
          type: "success",
          text: Vocabulary.wordpressSettingsSavedSuccessfully,
        });
      } else {
        throw new Error("Failed to save settings");
      }
    } catch (error) {
      console.error("Failed to save WordPress settings:", error);
      setMessage({
        type: "error",
        text: Vocabulary.failedToSaveWordpressSettings,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, margin: "20px auto" }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {Vocabulary.wordpressSettingsTitle}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {Vocabulary.wordpressSettingsDescription}
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Box component="form" noValidate autoComplete="off">
        <TextField
          fullWidth
          label={Vocabulary.wordpressSiteUrl}
          placeholder="https://wordpress-site.com"
          value={settings.WORDPRESS_URL}
          onChange={(e) => handleChange("WORDPRESS_URL", e.target.value)}
          margin="normal"
          helperText={Vocabulary.wordpressSiteUrlHelper}
          required
        />

        <TextField
          fullWidth
          label={Vocabulary.wooCommerceConsumerKey}
          placeholder="ck_..."
          value={settings.APP_CONSUMER_KEY}
          onChange={(e) => handleChange("APP_CONSUMER_KEY", e.target.value)}
          margin="normal"
          helperText={Vocabulary.wooCommerceConsumerKeyHelper}
          required
        />

        <TextField
          fullWidth
          label={Vocabulary.wooCommerceConsumerSecret}
          placeholder="cs_..."
          type="password"
          value={settings.APP_CONSUMER_SECRET}
          onChange={(e) => handleChange("APP_CONSUMER_SECRET", e.target.value)}
          margin="normal"
          helperText={Vocabulary.wooCommerceConsumerSecretHelper}
          required
        />

        <Box sx={{ mt: 4 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            <strong>{Vocabulary.howToGetWooCommerceCredentials}</strong>
            <br />
            1. {Vocabulary.wooCommerceStep1}
            <br />
            2. {Vocabulary.wooCommerceStep2}
            <br />
            3. {Vocabulary.wooCommerceStep3}
            <br />
            4. {Vocabulary.wooCommerceStep4}
            <br />
            5. {Vocabulary.wooCommerceStep5}
          </Typography>
        </Box>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? Vocabulary.saving : Vocabulary.saveSettings}
          </Button>

          <Button
            variant="outlined"
            onClick={getSettings}
            disabled={saving}
            size="large"
          >
            {Vocabulary.reset}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
